import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { Subscribe } from "unstated";
import { color_shades_lighter, color_shades_dark } from '../../constants/colors';
import TemplateFormFieldValueModel from '../../models/templating/api/TemplateFormFieldValueModel';
import TemplateFormFieldModel from '../../models/templating/api/TemplateFormFieldModel';
import TemplateFormSectionModel from '../../models/templating/api/TemplateFormSectionModel';
import CheckboxField from '../../components/forms/CheckboxField';
import TextField from '../../components/forms/TextField';
import TextAreaField from '../../components/forms/TextAreaField';
import SelectField from '../../components/forms/SelectField';
import Switch from '../../components/forms/Switch';
import FormFieldCard from '../../components/forms/FormFieldCard';
import PanelContainer from '../../components/structure/Panels/PanelContainer';
import TemplateInstructionsPanel from '../applicationAppliedTemplateManager/components/TemplateInstructionsPanel';
import ScrollablePanelBody from '../../components/structure/ScrollablePanelBody';
import RadioGroup from '../../components/forms/RadioGroup';
import CheckboxGroup from '../../components/forms/CheckboxGroup';
import SsmlField from '../../components/forms/SsmlField/SsmlField';
import FileUploadField from '../../components/forms/FileUploadField';
import MediaContainer from '../../state/containers/MediaContainer';
import CustomScrollbars from '../../components/structure/CustomScrollbars';
import SparkContainer from '../../hooks/SparkContainer';
import { getVisibleFields } from '../../utils/sectionUtils';
import TimeRangeField from '../../components/forms/TimeRangeField';
import SingleDayTimeRangeField from '../../components/forms/SingleDayTimeRangeField';
import SpecialDateField from '../../components/forms/SpecialDateField';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateModel from '../../models/templating/api/AppliedApplicationTemplateModel';
import { isDeprecatedSingleDayTimeRangeField, isDeprecatedSpecialDateField } from '../../utils/fieldTypeUtils';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import SaveFieldVariationModal from '../../components/forms/SaveFieldVariationModal';
import ViewFieldVariationsModal from '../../components/forms/ViewFieldVariationsModal';
import ApplicationVariable from '../../models/applications/ApplicationVariable';

interface AppliedApplicationTemplateFormProps {
  section: TemplateFormSectionModel
  onFieldValueChange: (field: TemplateFormFieldModel, value: string) => void
  applicationId: string
  appContainer: ApplicationContainer
  values?: TemplateFormFieldValueModel[]
  fieldsOnly?: boolean
  preview?: boolean
  recentlyAddedModuleFormFields?: TemplateFormFieldModel[]
  showAdminFields?: boolean
  appliedAppTemplate?: AppliedApplicationTemplateModel
  onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
}

const AppliedApplicationTemplateSectionForm: React.FC<AppliedApplicationTemplateFormProps> = (props) => {
  const sparkContainer = SparkContainer.useContainer();
  const variablesContainer = ApplicationVariablesContainer.useContainer();

  const [saveVariationModalVisible, setSaveVariationModalVisible] = useState(false);
  const [viewVariationsModalVisible, setViewVariationsModalVisible] = useState(false);
  const [selectedField, setSelectedField] = useState<TemplateFormFieldModel | null>(null);

  // Load application variables when component mounts or applicationId changes
  useEffect(() => {
    if (props.applicationId) {
      variablesContainer.loadApplicationVariablesByApplicationId(props.applicationId);
    }
  }, [props.applicationId]);

  const isRecentlyAddedModuleFormField = (field: TemplateFormFieldModel) => {
    return props.recentlyAddedModuleFormFields?.some(f => f.id == field.id);
  };

  const isFieldIncomplete = (field: TemplateFormFieldModel): boolean => {
    if (!field.isRequired) return false;
    const value = getValue(field);
    return !value || value.trim() === '';
  };

  const getValue = (field: TemplateFormFieldModel) => {
    const foundField = props.values?.find(v => (field?.name && v?.dynamicFormFieldName == field?.name) || (field?.id && v?.templateFormFieldId == field?.id));
    return foundField?.value;
  };

  const getFieldName = (field: TemplateFormFieldModel): string => {
    return field.name ?? field.id;
  };

  const handleChange = (field: TemplateFormFieldModel, value: string | boolean) => {
    props.onFieldValueChange(field, value.toString());
  };

  const handleReset = (field: TemplateFormFieldModel) => {
    if (field.defaultValue !== undefined) {
      props.onFieldValueChange(field, field.defaultValue);
    }
  };

  // Get variations for a specific field
  const getFieldVariations = (field: TemplateFormFieldModel): ApplicationVariable[] => {
    const prefix = `Form_Field_${field.title}_`;
    return variablesContainer.applicationVariables.filter(v => v.name.startsWith(prefix));
  };

  // Check if field has any saved variations
  const hasFieldVariations = (field: TemplateFormFieldModel): boolean => {
    return getFieldVariations(field).length > 0;
  };

  // Handle opening save variation modal
  const handleOpenSaveVariation = (field: TemplateFormFieldModel) => {
    setSelectedField(field);
    setSaveVariationModalVisible(true);
  };

  // Handle opening view variations modal
  const handleOpenViewVariations = (field: TemplateFormFieldModel) => {
    setSelectedField(field);
    setViewVariationsModalVisible(true);
  };


  // Handle selecting a variation to use
  const handleSelectVariation = (variation: ApplicationVariable) => {
    if (selectedField) {
      props.onFieldValueChange(selectedField, variation.value);
    }
    setViewVariationsModalVisible(false);
    setSelectedField(null);
  };

  const renderCheckbox = (field: TemplateFormFieldModel) => {
    return (
      <div>
        <div className={label}>{field.label}</div>
        <CheckboxField
          key={field.id ?? field.name}
          name={`fields.${getFieldName(field)}`}
          checked={getValue(field) == 'true'}
          onChange={(e) => handleChange(field, e.target.checked.toString())}
          className={checkboxField}
        />
      </div>
    )
  };

  const renderSwitch = (field: TemplateFormFieldModel) => {
    return (
      <div>
        <div className={label}>{field.label}</div>
        <Switch
          className={switchContainer}
          key={field.id ?? field.name}
          name={`fields.${getFieldName(field)}`}
          onChange={(checked) => handleChange(field, checked.toString())}
          checked={getValue(field) == 'true'}
          label={field.label}
        />
      </div>
    )
  };

  const renderShortText = (field: TemplateFormFieldModel, type?: string) => {
    if (isDeprecatedSingleDayTimeRangeField(field, props.appContainer, props.appliedAppTemplate)) {
      return (
        <SingleDayTimeRangeField
          key={field.id ?? field.name}
          name={`fields.${getFieldName(field)}`}
          label={field.label}
          value={getValue(field) ?? field.defaultValue}
          onChange={(value) => handleChange(field, value)}
          onBlur={() => { }}
          onValidationChange={(hasErrors) => {
            if (props.onFieldValidationChange) {
              props.onFieldValidationChange(field.id ?? field.name, hasErrors);
            }
          }}
        />
      )
    }

    if (isDeprecatedSpecialDateField(field, props.appContainer, props.appliedAppTemplate)) {
      return (
        <SpecialDateField
          key={field.id ?? field.name}
          name={`fields.${getFieldName(field)}`}
          label={field.label}
          value={getValue(field) ?? field.defaultValue}
          onChange={(value) => handleChange(field, value)}
          onBlur={() => { }}
          onValidationChange={(hasErrors) => {
            if (props.onFieldValidationChange) {
              props.onFieldValidationChange(field.id ?? field.name, hasErrors);
            }
          }}
        />
      )
    }

    return (
      <TextField
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        value={getValue(field) ?? field.defaultValue}
        defaultValue={field.defaultValue}
        type={type}
        onChange={(e) => handleChange(field, e.target.value)}
        onBlur={() => { }}
        onReset={() => handleReset(field)}
        label={field.label}
        placeholder={field.placeholder}
      />
    )
  };

  const renderLongText = (field: TemplateFormFieldModel) => {
    return (
      <TextAreaField
        showResponseVariableTest
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        value={getValue(field) ?? field.defaultValue}
        defaultValue={field.defaultValue}
        onChange={(e) => handleChange(field, e.target.value)}
        onBlur={() => { }}
        onReset={() => handleReset(field)}
        label={field.label}
        placeholder={field.placeholder}
      />
    )
  };

  const renderSsmlField = (field: TemplateFormFieldModel) => {
    return (
      <SsmlField
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        value={getValue(field) ?? field.defaultValue}
        onChange={(value) => handleChange(field, value)}
        onBlur={() => { }}
        label={field.label}
        placeholder={field.placeholder}
        applicationId={props.applicationId} />
    )
  };

  const renderSelect = (field: TemplateFormFieldModel) => {
    return (
      <SelectField
        key={field.id ?? field.name}
        className={selectField}
        onChange={(e: any) => handleChange(field, e.value)}
        onBlur={() => { }}
        name={`fields.${getFieldName(field)}`}
        value={getValue(field)}
        label={field.label}
        placeholder={field.placeholder}
        options={field.options.map(o => ({ value: o.value, label: o.label }))}
      />
    )
  };

  const renderCheckboxGroup = (field: TemplateFormFieldModel) => {
    // note: checkboxes use pipe delimited values from the core value
    return (
      <CheckboxGroup
        key={field.id ?? field.name}
        onChange={(e, c) => {
          var val = getValue(field);
          if (c) {
            val += `|${e.value}`;
          } else {
            val = val.replace(e.value, '');
          }
          handleChange(field, val)
        }}
        values={getValue(field)?.split('|')}
        options={field.options.map(o => ({ value: o.value, label: o.label }))}
      />
    )
  };

  const renderRadioGroup = (field: TemplateFormFieldModel) => {
    return (
      <RadioGroup
        key={field.id ?? field.name}
        onChange={(e) => handleChange(field, e.value)}
        value={getValue(field)}
        options={field.options.map(o => ({ value: o.value, label: o.label }))}
      />
    )
  };

  const renderFileUpload = (field: TemplateFormFieldModel) => {
    return (
      <Subscribe to={[MediaContainer]}>
        {(mediaContainer: MediaContainer) => (
          <FileUploadField
            onChange={(e) => handleChange(field, e.url)}
            disabled={false}
            field={field}
            mediaContainer={mediaContainer}
            applicationId={props.applicationId}
            value={getValue(field)}
            preview={props.preview}
          />
        )}
      </Subscribe>
    )
  };

  const renderTimeRange = (field: TemplateFormFieldModel) => {
    return (
      <TimeRangeField
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        label={field.label}
        value={getValue(field) ?? field.defaultValue}
        onChange={(value) => handleChange(field, value)}
        onBlur={() => { }}
      />
    )
  };

  const renderSingleDayTimeRange = (field: TemplateFormFieldModel) => {
    return (
      <SingleDayTimeRangeField
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        label={field.label}
        value={getValue(field) ?? field.defaultValue}
        onChange={(value) => handleChange(field, value)}
        onBlur={() => { }}
        onValidationChange={(hasErrors) => {
          if (props.onFieldValidationChange) {
            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
          }
        }}
      />
    )
  };

  const renderSpecialDate = (field: TemplateFormFieldModel) => {
    return (
      <SpecialDateField
        key={field.id ?? field.name}
        name={`fields.${getFieldName(field)}`}
        label={field.label}
        value={getValue(field) ?? field.defaultValue}
        onChange={(value) => handleChange(field, value)}
        onBlur={() => { }}
        onValidationChange={(hasErrors) => {
          if (props.onFieldValidationChange) {
            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
          }
        }}
      />
    )
  };

  // Check if field type supports variations (text-based fields only)
  const supportsVariations = (field: TemplateFormFieldModel): boolean => {
    const textBasedFieldTypes = [
      "ShortTextField",
      "NumberTextField",
      "DateTextField",
      "LongTextField",
      "SSMLField",
      "SSMLTextField"
    ];
    return textBasedFieldTypes.includes(field.fieldType);
  };

  const renderSetupProperty = (field: TemplateFormFieldModel) => {
    switch (field.fieldType) {
      case "ShortTextField":
        return renderShortText(field);
      case "NumberTextField":
        return renderShortText(field, 'number');
      case "DateTextField":
        return renderShortText(field, 'date');
      case "LongTextField":
        return renderLongText(field);
      case "SwitchField":
        return renderSwitch(field);
      case "SelectField":
        return renderSelect(field);
      case "CheckboxGroupField":
        return renderCheckboxGroup(field);
      case "RadioGroupField":
        return renderRadioGroup(field);
      case "CheckboxField":
        return renderCheckbox(field);
      case "FileUploadField":
        return renderFileUpload(field);
      case "TimeRangeField":
        return renderTimeRange(field);
      case "SingleDayTimeRangeField":
        return renderSingleDayTimeRange(field);
      case "SpecialDateField":
        return renderSpecialDate(field);
      case "SSMLField":
      case "SSMLTextField":
        return renderSsmlField(field);
    }
  };

  const renderFields = () => {
    // Use the consolidated helper function to get visible fields
    // Note: In non-Spark contexts, showAdminFields defaults to true
    const showAdmin = sparkContainer.inSpark ? (props.showAdminFields ?? false) : true;
    const fields = getVisibleFields(
      props.section,
      showAdmin,
      props.values
    );

    return (
      <div className={setupSectionContainer}>
        <h3>{props.section.title}</h3>
        {fields.map((p, subIdx) => {
          const fieldSupportsVariations = supportsVariations(p);
          return (
            <div key={`setupSection_${subIdx}`} className={setupPropertyContainer}>
              <FormFieldCard
                title={p.title}
                tip={p.tip}
                required={p.isRequired}
                isIncomplete={isFieldIncomplete(p)}
                isRecentlyAddedModuleFormField={isRecentlyAddedModuleFormField(p)}
                hasVariations={fieldSupportsVariations ? hasFieldVariations(p) : false}
                onSaveVariation={fieldSupportsVariations ? () => handleOpenSaveVariation(p) : undefined}
                onViewVariations={fieldSupportsVariations ? () => handleOpenViewVariations(p) : undefined}
              >
                {renderSetupProperty(p)}
              </FormFieldCard>
            </div>
          )
        })}
      </div>
    )
  };

  return (
    <>
      {props.fieldsOnly && renderFields()}
      <PanelContainer className={panelContainerStyle}>
        <CustomScrollbars autoHide={true} autoHeight={true} autoHeightMin="calc(100vh - 136px)" autoHeightMax="calc(100vh - 136px)">
          {renderFields()}
        </CustomScrollbars>
        {props.section.instructionsMarkdown &&
          <ScrollablePanelBody className={previewStyle}>
            <TemplateInstructionsPanel
              title="Preview"
              instructionsMarkdown={props.section.instructionsMarkdown}
            />
          </ScrollablePanelBody>
        }
      </PanelContainer>

      {/* Variation Modals */}
      {selectedField && (
        <>
          <SaveFieldVariationModal
            isVisible={saveVariationModalVisible}
            fieldTitle={selectedField.title}
            fieldValue={getValue(selectedField) || ''}
            onClose={() => {
              setSaveVariationModalVisible(false);
              setSelectedField(null);
            }}
            applicationId={props.applicationId}
          />
          <ViewFieldVariationsModal
            isVisible={viewVariationsModalVisible}
            fieldTitle={selectedField.title}
            variations={getFieldVariations(selectedField)}
            onClose={() => {
              setViewVariationsModalVisible(false);
              setSelectedField(null);
            }}
            onSelectVariation={handleSelectVariation}
          />
        </>
      )}
    </>
  )
}

const panelContainerStyle = css`
`

const previewStyle = css`
    height: 100%;
`

const selectField = css`
    padding-top: 16px;
`;

const checkboxField = css`
    padding-left: 16px;
    .tooltip {
        margin-top: 3px;
    }
`;

const setupSectionContainer = css`
    padding: 32px;
    h3 {
        margin-bottom: 16px;
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
    }
`;

const label = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    text-align: left;
    margin: 8px 16px;
`;
const switchContainer = css`
    margin-bottom: 32px;
`
const setupPropertyContainer = css`
    padding: 16px 0px;
    position: relative;
    .field-triangle {
        position: absolute;
        width: 24px;
        height: 24px;
        background: ${color_shades_lighter};
        right: -44px;
        top: 64px;
        transform: rotate(45deg);
        border-left: 1px solid ${color_shades_dark};
        border-bottom: 1px solid ${color_shades_dark};
    }
`;

export default AppliedApplicationTemplateSectionForm;